import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { InfrastructureTableContext } from './InfrastructureTableContext';
import { useInfrastructureData } from '../InfrastructureDataContext';
import { useArrayLoader } from '../../../data-table/Loaders';
import DataTable from '../../../data-table/DataTable';
import ListActionSelector from '../../../form/ListActionSelector';
import { SELECTIONMODE_MULTI } from '../../../data-table/SelectionContext';

/**
 * Content compound component for InfrastructureTable.
 *
 * Renders the actual table content (either DataTable for read-only mode or
 * ListActionSelector for interactive mode with selection and actions).
 *
 * This component consumes the InfrastructureTableContext and detects sibling
 * **Usage:**
 * ```jsx
 * // Read-only mode with DataTable
 * <InfrastructureTable {...props}>
 *   <InfrastructureTable.Content />
 * </InfrastructureTable>
 *
 * // Interactive mode with ListActionSelector
 * <InfrastructureTable {...props}>
 *   <InfrastructureTable.Content enableSelection actionMenus={menus} />
 * </InfrastructureTable>
 * ```
 *
 * @param {Object} props
 * @param {boolean} [props.enableSelection=false] - Use ListActionSelector instead of DataTable
 */
const Content = ({ enableSelection = false, setTableState = noop }) => {
    // Use granular data context instead of combined context for better performance
    const { data, columns, emptyText } = useInfrastructureData();

    // Fallback to combined context for properties not in data context
    const context = useContext(InfrastructureTableContext);

    if (!context) {
        throw new Error(
            'Content must be used within InfrastructureTable. ' +
                'Wrap your Content component in <InfrastructureTable>...</InfrastructureTable>'
        );
    }

    const {
        tableId,
        actionMenus,
        onSelectionChange,
        visibleRows,
        dataArraySearchFields,
        isActioning,
        lastLoadDataArrayTime,
    } = context;

    // Prepare loader for DataTable (must be at top level per Hook rules)
    const loader = useArrayLoader(data, (d) => d);

    // Render DataTable for read-only mode
    const renderDataTable = () => (
        <DataTable
            tableId={tableId}
            onStateChanged={setTableState}
            columns={columns}
            emptyText={emptyText}
            loader={loader}
            disableColumnRemover
            disableCSVExport
        />
    );

    // Render ListActionSelector for interactive mode (selection + actions)
    const renderListActionSelector = () => (
        <ListActionSelector
            tableId={tableId}
            onStateChanged={setTableState}
            model={SELECTIONMODE_MULTI}
            gridColumns={columns}
            dataArray={data}
            changeSelectionId={onSelectionChange}
            actionMenus={actionMenus}
            isActioning={isActioning}
            emptyText={emptyText}
            visibleRows={visibleRows}
            lastLoadDataArrayTime={lastLoadDataArrayTime}
            dataArraySearchFields={dataArraySearchFields}
        />
    );

    return enableSelection ? renderListActionSelector() : renderDataTable();
};

Content.propTypes = {
    enableSelection: PropTypes.bool,
    setTableState: PropTypes.func,
};

Content.defaultProps = {
    enableSelection: false,
    setTableState: noop,
};

// Memoize to prevent re-renders when parent context changes but props are stable
export default React.memo(Content);
